// LogoVeterinaria.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import api from '../api/api';

const MAX_SIZE_MB = 2;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PRIMARY_COLOR = '#4f46e5';
const DANGER_COLOR = '#ef4444';
const SECONDARY_COLOR = '#6b7280';

const LogoVeterinaria = ({
  open,
  onClose,
  onSuccess,
  currentLogoFilename, // string | null
}) => {
  const inputRef = useRef(null);
  const [archivo, setArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [previewLoadError, setPreviewLoadError] = useState(false);

  useEffect(() => {
    if (open) {
      setArchivo(null);
      setError('');
      setSuccessMsg('');
      setPreviewLoadError(false);

      const initialPreview = currentLogoFilename
        ? `/static/logos/${currentLogoFilename}`
        : null;

      setPreview(initialPreview);
    }

    // Limpieza al cerrar
    return () => {
      if (!open) {
        setPreview(null);
      }
    };
  }, [open]); // ← solo open → rompe el bucle

  const handleArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Solo PNG, JPG o WebP permitidos.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Máximo ${MAX_SIZE_MB} MB.`);
      return;
    }

    setError('');
    setArchivo(file);
    setPreview(URL.createObjectURL(file));
    setPreviewLoadError(false);
  };

  const handleGuardar = async () => {
    if (!archivo) return;
    setUploading(true);
    setError('');
    setSuccessMsg('');

    try {
      const fd = new FormData();
      fd.append('imagen', archivo);

      const response = await api.post('/logos/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const nuevoFilename = response.data.url_logo;
      setSuccessMsg('Logo actualizado correctamente');
      onSuccess(nuevoFilename);
      setArchivo(null);
      setPreview(`/static/logos/${nuevoFilename}`);
      setPreviewLoadError(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setUploading(false);
    }
  };

  const handleEliminar = async () => {
    if (!currentLogoFilename) return;
    if (!window.confirm('¿Eliminar el logo actual?')) return;

    setDeleting(true);
    setError('');

    try {
      // Ajusta esta ruta según tu backend real
      await api.patch('/pet/update-logo', { url_logo: null });

      setSuccessMsg('Logo eliminado');
      onSuccess(null);
      setPreview(null);
      setArchivo(null);
      setPreviewLoadError(false);
    } catch (err) {
      setError('No se pudo eliminar el logo');
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (uploading || deleting) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={uploading || deleting}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Gestionar Logo de la Clínica
        <IconButton onClick={handleClose} disabled={uploading || deleting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          onClick={() => !uploading && !deleting && inputRef.current?.click()}
          sx={{
            border: `2px dashed ${preview ? PRIMARY_COLOR : SECONDARY_COLOR}`,
            borderRadius: 3,
            p: 4,
            mb: 3,
            textAlign: 'center',
            cursor: preview || uploading || deleting ? 'default' : 'pointer',
            bgcolor: preview ? '#eef2ff' : '#f8fafc',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_TYPES.join(',')}
            style={{ display: 'none' }}
            onChange={handleArchivo}
            disabled={uploading || deleting}
          />

          {preview ? (
            <>
              <img
                src={preview}
                alt="Previsualización del logo"
                style={{
                  maxHeight: 180,
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
                onLoad={() => setPreviewLoadError(false)}
                onError={() => {
                  setPreviewLoadError(true);
                  // Opcional: fallback a placeholder
                  // e.target.src = '/static/placeholder-logo.png';
                }}
              />
              {previewLoadError && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 12,
                    fontSize: '0.85rem',
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  No se pudo cargar la imagen
                </Box>
              )}
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: SECONDARY_COLOR }}>
                {archivo ? archivo.name : 'Logo actual'}
              </Typography>
            </>
          ) : (
            <>
              <ImageIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Haz clic para seleccionar un logo
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PNG / JPG / WebP • máx. {MAX_SIZE_MB} MB • fondo transparente recomendado
              </Typography>
            </>
          )}

          {uploading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
        {uploading && <LinearProgress sx={{ mb: 2 }} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 2 }}>
        {currentLogoFilename && preview && (
          <Tooltip title="Eliminar logo actual">
            <Button
              color="error"
              startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              onClick={handleEliminar}
              disabled={uploading || deleting}
              variant="outlined"
            >
              Eliminar logo
            </Button>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        <Button onClick={handleClose} disabled={uploading || deleting}>
          Cerrar
        </Button>

        <Button
          variant="contained"
          disabled={!archivo || uploading || deleting}
          onClick={handleGuardar}
          startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
          sx={{ bgcolor: PRIMARY_COLOR }}
        >
          {uploading ? 'Subiendo...' : 'Subir / Actualizar logo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogoVeterinaria;